import React, { useEffect, useState } from 'react';
import { Mail, Phone, Calendar, FileText, Eye } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateMdY, formatDateTimeMdY } from '@/utils/dateFormat';
import {
  LEAD_TYPE_LABELS,
  getLeadTypeLabel,
  getDealStatusLabel,
  DEAL_STATUS_LABELS,
} from '@/constants/adminVocabulary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export { LEAD_TYPE_LABELS, getLeadTypeLabel };

export const DEAL_SYNCED_LEAD_STATUS_STYLES = {
  approval_pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  approved: 'bg-green-500/15 text-green-400 border-green-500/30',
  ready_to_close: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  closing: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  funded: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  delivered: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  filed_taxes: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  archived: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  credit_pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

export const PRE_DEAL_LEAD_STATUS_STYLES = {
  new: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  pending_review: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  pending_evaluation: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  contacted: 'bg-green-500/10 text-green-400 border-green-500/30',
  closed: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
};

export const getLeadStatusColor = (status) => {
  return (
    DEAL_SYNCED_LEAD_STATUS_STYLES[status]
    || PRE_DEAL_LEAD_STATUS_STYLES[status]
    || PRE_DEAL_LEAD_STATUS_STYLES.new
  );
};

const PRE_DEAL_STATUS_LABELS = {
  pending_review: 'Pending Review',
  pending_evaluation: 'Pending Evaluation',
};

export const formatLeadStatusLabel = (status) => {
  if (!status) return 'Unknown';
  if (DEAL_STATUS_LABELS[status]) return getDealStatusLabel(status);
  if (PRE_DEAL_STATUS_LABELS[status]) return PRE_DEAL_STATUS_LABELS[status];
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const LEAD_CONVERTED_EDIT_DENIED_MESSAGE =
  'This lead has been converted to a deal. Status and customer entries can only be modified in Deals Hub from this point forward.';

export const PRE_DEAL_STATUS_OPTIONS_BY_TYPE = {
  contact: ['new', 'contacted', 'closed'],
  vehicle_inquiry: ['new', 'contacted', 'closed'],
  financing: ['pending_review', 'contacted', 'closed'],
  trade_in: ['pending_evaluation', 'contacted', 'closed'],
  prequalification: ['pending_review', 'contacted', 'closed'],
  chatbot: ['new', 'contacted', 'closed'],
};

export const formatLeadDate = (dateString) => formatDateTimeMdY(dateString);

export const formatLast4 = (last4) => {
  const tail = last4 ? String(last4).replace(/\D/g, '').slice(-4) : '';
  return tail ? `***-**-${tail}` : 'Not provided';
};

export const formatDlLast4 = (last4) => {
  const tail = last4 ? String(last4).replace(/\D/g, '').slice(-4) : '';
  return tail ? `••••${tail}` : 'Not provided';
};

export const maskSSN = (ssn, last4) => formatLast4(last4 || ssn);

export function SensitiveNpiField({
  label,
  last4,
  leadId,
  source = 'lead',
  field = 'ssn',
  subject = 'applicant',
  kind = 'ssn',
}) {
  const { token, canRevealNpi } = useAuth();
  const [fullValue, setFullValue] = useState('');
  const [loading, setLoading] = useState(false);
  const display = kind === 'dl' ? formatDlLast4(last4) : formatLast4(last4);

  useEffect(() => () => setFullValue(''), [leadId, field, subject]);

  const reveal = async () => {
    if (!token || !leadId) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/pii/reveal`,
        { source, id: leadId, fields: [field], subject },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const value = field === 'ssn' ? res.data.ssn : res.data.driver_license_number;
      setFullValue(value || '');
      window.setTimeout(() => setFullValue(''), 30000);
    } catch (err) {
      setFullValue('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">{label}</label>
      <div className="flex items-center gap-2">
        <p className="font-['Manrope'] text-white font-mono text-sm">{fullValue || display}</p>
        {canRevealNpi && last4 && !fullValue && (
          <button
            type="button"
            onClick={reveal}
            disabled={loading}
            className="text-[10px] font-['Manrope'] text-amber-400 hover:text-amber-300 flex items-center gap-1"
          >
            <Eye className="w-3 h-3" />
            {loading ? 'Revealing…' : 'Reveal'}
          </button>
        )}
      </div>
    </div>
  );
}

export const formatCurrency = (value) => {
  if (!value) return 'N/A';
  const num = parseInt(value.toString().replace(/[^0-9]/g, ''), 10);
  return Number.isNaN(num) ? value : `$${num.toLocaleString()}`;
};

export function FieldDisplay({ label, value, highlight, mono, className = '' }) {
  return (
    <div className={className}>
      <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">
        {label}
      </label>
      <p className={`font-['Manrope'] ${highlight ? 'text-white font-semibold' : 'text-white'} ${mono ? 'font-mono text-sm' : ''}`}>
        {value || 'N/A'}
      </p>
    </div>
  );
}

const isBlank = (value) => value == null || String(value).trim() === '';

const normalizeForCompare = (value) => {
  if (isBlank(value)) return '';
  const raw = String(value).trim();
  const compact = raw.replace(/\s+/g, ' ');
  const digits = compact.replace(/\D/g, '');
  if (digits.length >= 10 && /^[\d\s().+\-]+$/.test(compact)) return digits.slice(-10);
  if (/^\d{3}-?\d{2}-?\d{4}$/.test(compact.replace(/\s/g, ''))) return digits;
  if (/^\d{5}(-\d{4})?$/.test(compact)) return compact.slice(0, 5);
  const parsed = Date.parse(compact);
  const looksLikeDate = compact.includes('-') || compact.includes('/') || compact.includes(',');
  if (!Number.isNaN(parsed) && /\d/.test(compact) && looksLikeDate) {
    return new Date(parsed).toISOString().slice(0, 10);
  }
  return compact.toLowerCase();
};

const valuesMatch = (left, right) => normalizeForCompare(left) === normalizeForCompare(right);

const joinAddress = (info = {}) => `${info.addressLine1 || ''} ${info.addressLine2 || ''}`.trim();

function DualSourceField({ label, financingValue, tradeInValue, format, highlight, mono, className = '' }) {
  const financingEmpty = isBlank(financingValue);
  const tradeInEmpty = isBlank(tradeInValue);

  if (financingEmpty && tradeInEmpty) {
    return <FieldDisplay label={label} value={null} highlight={highlight} mono={mono} className={className} />;
  }
  if (financingEmpty || tradeInEmpty || valuesMatch(financingValue, tradeInValue)) {
    const raw = financingEmpty ? tradeInValue : financingValue;
    return (
      <FieldDisplay
        label={label}
        value={format ? format(raw) : raw}
        highlight={highlight}
        mono={mono}
        className={className}
      />
    );
  }

  return (
    <div className={className}>
      <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">{label}</label>
      <p className={`font-['Manrope'] ${highlight ? 'text-white font-semibold' : 'text-white'} ${mono ? 'font-mono text-sm' : ''}`}>
        {format ? format(financingValue) : financingValue}
        <span className="ml-2 text-[10px] uppercase tracking-wide text-blue-400">Financing</span>
      </p>
      <p className={`mt-1 font-['Manrope'] ${highlight ? 'text-white font-semibold' : 'text-white'} ${mono ? 'font-mono text-sm' : ''}`}>
        {format ? format(tradeInValue) : tradeInValue}
        <span className="ml-2 text-[10px] uppercase tracking-wide text-orange-400">Trade-In</span>
      </p>
    </div>
  );
}

export function TradeInVehicleFields({ lead }) {
  const vehicleInfo = lead?.details?.vehicleInfo;
  if (!vehicleInfo && !lead?.details?.vehicle) return null;

  const mileageRaw = vehicleInfo?.mileage || lead.details?.mileage;
  const mileage = mileageRaw
    ? `${parseInt(mileageRaw, 10).toLocaleString()} mi`
    : null;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldDisplay
          label="Vehicle"
          value={lead.details?.vehicle || `${vehicleInfo?.year || ''} ${vehicleInfo?.make || ''} ${vehicleInfo?.model || ''}`.trim()}
          highlight
        />
        <FieldDisplay label="Trim" value={vehicleInfo?.trim} />
        <FieldDisplay label="Mileage" value={mileage} />
        <FieldDisplay label="VIN" value={vehicleInfo?.vin || lead.details?.vin} mono />
        <FieldDisplay label="Year" value={vehicleInfo?.year} />
        <FieldDisplay label="Make" value={vehicleInfo?.make} />
        <FieldDisplay label="Model" value={vehicleInfo?.model} />
        <FieldDisplay label="Transmission" value={vehicleInfo?.transmission} />
        <FieldDisplay label="Color" value={vehicleInfo?.colour} />
        <FieldDisplay label="Body Style" value={vehicleInfo?.body_style} />
        <FieldDisplay label="Accidents" value={vehicleInfo?.accidents || lead.details?.accidents} />
        <FieldDisplay label="Financed" value={vehicleInfo?.financed || lead.details?.financed} />
        <FieldDisplay label="Financed With" value={vehicleInfo?.financedWithWhom} />
        <FieldDisplay label="Payoff Amount" value={formatCurrency(vehicleInfo?.payOffAmount)} />
      </div>
      {lead.details?.vehicleFeatures?.length > 0 && (
        <div className="mt-4">
          <FieldDisplay label="Features" value={lead.details.vehicleFeatures.join(', ')} />
        </div>
      )}
    </>
  );
}

function renderMergedPersonalInformation(applicant = {}, personal = {}, leadId) {
  return (
    <div>
      <h3 className="text-sm font-['Unbounded'] font-semibold text-blue-400 mb-4 flex items-center gap-2">
        <FileText className="w-4 h-4" />
        Personal Information
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DualSourceField label="First Name" financingValue={applicant.firstName} tradeInValue={personal.firstName} />
        <DualSourceField label="Middle Name" financingValue={applicant.middleName} tradeInValue={personal.middleName} />
        <DualSourceField label="Last Name" financingValue={applicant.lastName} tradeInValue={personal.lastName} />
        <DualSourceField label="Date of Birth" financingValue={applicant.dateOfBirth} tradeInValue={personal.dateOfBirth} format={formatDateMdY} />
        <SensitiveNpiField label="SSN" last4={applicant.ssn_last4 || personal.ssn_last4} leadId={leadId} field="ssn" />
        <DualSourceField label="Mobile Phone" financingValue={applicant.mobileNumber} tradeInValue={personal.mobileNumber} />
        <DualSourceField label="Home Phone" financingValue={applicant.homeNumber} tradeInValue={personal.homeNumber} />
        <DualSourceField label="Email" financingValue={applicant.email} tradeInValue={personal.email} />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DualSourceField
          label="Address"
          financingValue={joinAddress(applicant)}
          tradeInValue={joinAddress(personal)}
          className="col-span-2"
        />
        <DualSourceField label="City" financingValue={applicant.city} tradeInValue={personal.city} />
        <DualSourceField label="State" financingValue={applicant.state} tradeInValue={personal.state} />
        {!isBlank(personal.county) && (
          <DualSourceField label="County" financingValue={applicant.county} tradeInValue={personal.county} />
        )}
        <DualSourceField label="ZIP Code" financingValue={applicant.zip} tradeInValue={personal.zip} />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SensitiveNpiField label="Driver License #" last4={applicant.driverLicenseLast4 || personal.driverLicenseLast4} leadId={leadId} field="driver_license" kind="dl" />
        <DualSourceField label="DL State" financingValue={applicant.driverLicenseState} tradeInValue={personal.driverLicenseState} />
        <DualSourceField label="DL Issuing County" financingValue={applicant.dlIssuingCounty} tradeInValue={personal.dlIssuingCounty} />
        <DualSourceField label="DL Expiry" financingValue={applicant.driverLicenseExpiry} tradeInValue={personal.driverLicenseExpiry} format={formatDateMdY} />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DualSourceField label="Residence Type" financingValue={applicant.residenceType} tradeInValue={personal.residenceType} />
        <DualSourceField
          label="Rent/Mortgage"
          financingValue={applicant.rentMortgage}
          tradeInValue={personal.rentMortgage}
          format={formatCurrency}
        />
        <FieldDisplay
          label="Time at Residence"
          value={`${applicant.residenceYears || 0} years, ${applicant.residenceMonths || 0} months`}
        />
      </div>
    </div>
  );
}

function renderFinancingDetails(lead, { showVehicleInterest = true, mergePersonalFrom = null } = {}) {
  const applicant = lead.details?.applicant || {};
  const employment = lead.details?.employment || {};
  const vehicle = lead.details?.vehicle || {};
  const coBuyer = lead.details?.coBuyer;
  const merged = mergePersonalFrom != null;

  return (
    <div className="space-y-6">
      {merged ? renderMergedPersonalInformation(applicant, mergePersonalFrom || {}, lead.id) : (
      <div>
        <h3 className="text-sm font-['Unbounded'] font-semibold text-blue-400 mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Applicant Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldDisplay label="First Name" value={applicant.firstName} />
          <FieldDisplay label="Middle Name" value={applicant.middleName} />
          <FieldDisplay label="Last Name" value={applicant.lastName} />
          <FieldDisplay label="Date of Birth" value={formatDateMdY(applicant.dateOfBirth, '')} />
          <SensitiveNpiField label="SSN" last4={applicant.ssn_last4} leadId={lead.id} field="ssn" />
          <FieldDisplay label="Mobile Phone" value={applicant.mobileNumber} />
          <FieldDisplay label="Home Phone" value={applicant.homeNumber} />
          <FieldDisplay label="Email" value={applicant.email} />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldDisplay label="Address" value={`${applicant.addressLine1 || ''} ${applicant.addressLine2 || ''}`.trim()} className="col-span-2" />
          <FieldDisplay label="City" value={applicant.city} />
          <FieldDisplay label="State" value={applicant.state} />
          <FieldDisplay label="ZIP Code" value={applicant.zip} />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SensitiveNpiField label="Driver License #" last4={applicant.driverLicenseLast4} leadId={lead.id} field="driver_license" kind="dl" />
          <FieldDisplay label="DL State" value={applicant.driverLicenseState} />
          <FieldDisplay label="DL Issuing County" value={applicant.dlIssuingCounty} />
          <FieldDisplay label="DL Expiry" value={formatDateMdY(applicant.driverLicenseExpiry, '')} />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldDisplay label="Residence Type" value={applicant.residenceType} />
          <FieldDisplay label="Rent/Mortgage" value={formatCurrency(applicant.rentMortgage)} />
          <FieldDisplay label="Time at Residence" value={`${applicant.residenceYears || 0} years, ${applicant.residenceMonths || 0} months`} />
        </div>
      </div>
      )}

      <div className="border-t border-white/10 pt-6">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-green-400 mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Employment Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldDisplay label="Employer" value={employment.employer} />
          <FieldDisplay label="Employer Type" value={employment.employerType} />
          <FieldDisplay label="Occupation" value={employment.occupation} />
          <FieldDisplay label="Monthly Income" value={formatCurrency(employment.monthlyIncome)} highlight />
          <FieldDisplay label="Work Phone" value={employment.workPhone} />
          <FieldDisplay label="Time at Job" value={`${employment.jobYears || 0} years, ${employment.jobMonths || 0} months`} />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldDisplay label="Work Address" value={`${employment.addressLine1 || ''} ${employment.addressLine2 || ''}`.trim()} className="col-span-2" />
          <FieldDisplay label="City" value={employment.city} />
          <FieldDisplay label="State" value={employment.state} />
          <FieldDisplay label="ZIP Code" value={employment.zip} />
        </div>
      </div>

      {showVehicleInterest && (
      <div className="border-t border-white/10 pt-6">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-purple-400 mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Vehicle Interest
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldDisplay label="Year" value={vehicle.year} />
          <FieldDisplay label="Make" value={vehicle.make} />
          <FieldDisplay label="Model" value={vehicle.model} />
          <FieldDisplay label="Trim" value={vehicle.trim} />
          <FieldDisplay label="VIN" value={vehicle.vin} />
          <FieldDisplay label="Stock Number" value={vehicle.stockNumber} />
          <FieldDisplay label="Mileage" value={vehicle.mileage} />
        </div>
      </div>
      )}

      {coBuyer && coBuyer.info && (
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-sm font-['Unbounded'] font-semibold text-orange-400 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Co-Buyer Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldDisplay label="First Name" value={coBuyer.info.firstName} />
            <FieldDisplay label="Last Name" value={coBuyer.info.lastName} />
            <FieldDisplay label="Date of Birth" value={formatDateMdY(coBuyer.info.dateOfBirth, '')} />
            <SensitiveNpiField label="SSN" last4={coBuyer.info.ssn_last4} leadId={lead.id} field="ssn" subject="co_buyer" />
            <FieldDisplay label="Mobile Phone" value={coBuyer.info.mobileNumber} />
            <FieldDisplay label="Email" value={coBuyer.info.email} />
          </div>
          {coBuyer.employment && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldDisplay label="Employer" value={coBuyer.employment.employer} />
              <FieldDisplay label="Occupation" value={coBuyer.employment.occupation} />
              <FieldDisplay label="Monthly Income" value={formatCurrency(coBuyer.employment.monthlyIncome)} highlight />
            </div>
          )}
        </div>
      )}

      {lead.details?.additionalComments && (
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4">
            Additional Comments
          </h3>
          <p className="text-white font-['Manrope'] bg-white/5 p-3 border border-white/10">
            {lead.details.additionalComments}
          </p>
        </div>
      )}
    </div>
  );
}

function renderTradeInDetails(lead, { showTradeInVehicle = true, hidePersonalInfo = false, showDescription = true } = {}) {
  const personal = lead.details?.personalInfo;
  const hasTradeInVehicle = Boolean(lead.details?.vehicleInfo || lead.details?.vehicle);

  return (
    <div className="space-y-6">
      {personal && !hidePersonalInfo && (
        <div>
          <h3 className="text-sm font-['Unbounded'] font-semibold text-blue-400 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Personal Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldDisplay label="First Name" value={personal.firstName} />
            <FieldDisplay label="Last Name" value={personal.lastName} />
            <FieldDisplay label="Email" value={personal.email} />
            <FieldDisplay label="Mobile Phone" value={personal.mobileNumber} />
            <FieldDisplay label="Home Phone" value={personal.homeNumber} />
            <FieldDisplay label="Date of Birth" value={formatDateMdY(personal.dateOfBirth, '')} />
            <SensitiveNpiField label="Driver License #" last4={personal.driverLicenseLast4} leadId={lead.id} field="driver_license" kind="dl" />
            <FieldDisplay label="DL State" value={personal.driverLicenseState} />
            <FieldDisplay label="DL Expiry" value={formatDateMdY(personal.driverLicenseExpiry, '')} />
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldDisplay label="Address" value={`${personal.addressLine1 || ''} ${personal.addressLine2 || ''}`.trim()} className="col-span-2" />
            <FieldDisplay label="City" value={personal.city} />
            <FieldDisplay label="State" value={personal.state} />
            <FieldDisplay label="County" value={personal.county} />
            <FieldDisplay label="ZIP Code" value={personal.zip} />
          </div>
        </div>
      )}

      {showTradeInVehicle && hasTradeInVehicle && (
        <div className={personal && !hidePersonalInfo ? 'border-t border-white/10 pt-6' : ''}>
          <h3 className="text-sm font-['Unbounded'] font-semibold text-purple-400 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Trade-In Vehicle
          </h3>
          <TradeInVehicleFields lead={lead} />
        </div>
      )}

      {showDescription && lead.details?.description && (
        <div className="border-t border-white/10 pt-6">
          <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">
            Description
          </label>
          <p className="text-white font-['Manrope'] bg-white/5 p-3 border border-white/10">
            {lead.details.description}
          </p>
        </div>
      )}
    </div>
  );
}

function ChatbotMessageNotes({ lead }) {
  const [showFullChat, setShowFullChat] = useState(false);
  const messages = lead.details?.chat_messages || [];
  const reason = lead.details?.capture_reason || 'Visitor shared contact information with Francisco.';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1">
        <label className="text-sm font-['Manrope'] text-white/60 block">Message Notes</label>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => setShowFullChat((open) => !open)}
            className="px-3 py-1 text-xs font-['Manrope'] font-semibold border border-white/15 bg-white/5 hover:bg-white/10 text-white"
            data-testid="display-full-chat-btn"
          >
            {showFullChat ? 'Hide Full Chat' : 'Display Full Chat'}
          </button>
        )}
      </div>
      <div className="text-white font-['Manrope'] bg-white/5 p-3 border border-white/10 space-y-3">
        <p className="text-sm text-white/90">{reason}</p>
        {showFullChat && (
          <div className="space-y-2 pt-2 border-t border-white/10">
            <p className="text-xs uppercase tracking-wider text-white/40">Full chat</p>
            {messages.map((msg, index) => (
              <div key={`${msg.role}-${index}`} className="text-sm">
                <span className={msg.role === 'user' ? 'text-blue-400' : 'text-emerald-400'}>
                  {msg.role === 'user' ? 'Visitor' : 'Francisco'}
                </span>
                <p className="text-white/90 whitespace-pre-wrap mt-0.5">{msg.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function renderTypeSpecificDetails(lead, options = {}) {
  switch (lead.type) {
    case 'contact':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">
              Message
            </label>
            <p className="text-white font-['Manrope'] bg-white/5 p-3 border border-white/10">
              {lead.details?.message || 'No message provided'}
            </p>
          </div>
          <div>
            <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">
              SMS / call consent
            </label>
            <p className="text-white font-['Manrope']">
              {lead.details?.consent ? 'Yes' : 'No'}
            </p>
          </div>
        </div>
      );

    case 'vehicle_inquiry':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">
              Vehicle Interest
            </label>
            <p className="text-white font-['Manrope'] font-semibold">
              {lead.details?.vehicle_info}
            </p>
          </div>
          <div>
            <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">
              Preferred Contact
            </label>
            <p className="text-white font-['Manrope']">
              {lead.details?.preferred_contact || 'email'}
            </p>
          </div>
          <div>
            <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">
              SMS / call consent
            </label>
            <p className="text-white font-['Manrope']">
              {lead.details?.consent ? 'Yes' : 'No'}
            </p>
          </div>
          {lead.details?.message && (
            <div>
              <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">
                Message
              </label>
              <p className="text-white font-['Manrope'] bg-white/5 p-3 border border-white/10">
                {lead.details.message}
              </p>
            </div>
          )}
        </div>
      );

    case 'financing':
      return renderFinancingDetails(lead, options);

    case 'trade_in':
      return renderTradeInDetails(lead, options);

    case 'chatbot':
      return (
        <div className="space-y-4">
          {lead.details?.vehicle_info && (
            <div>
              <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">Vehicle of interest</label>
              <p className="text-white font-['Manrope'] font-semibold">{lead.details.vehicle_info}</p>
            </div>
          )}
          {lead.details?.page_url && (
            <div>
              <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">Page</label>
              <p className="text-white/80 font-['Manrope'] text-sm break-all">{lead.details.page_url}</p>
            </div>
          )}
          <ChatbotMessageNotes lead={lead} />
          <FieldDisplay label="SMS / call consent" value={lead.details?.consent ? "Yes" : "No"} />
        </div>
      );

    case 'prequalification':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldDisplay label="Address" value={lead.details?.address} />
            <FieldDisplay label="Date of Birth" value={formatDateMdY(lead.details?.dateOfBirth, '')} />
            <FieldDisplay label="Job Duration" value={lead.details?.jobDuration} />
            <FieldDisplay label="Monthly Income" value={formatCurrency(lead.details?.monthlyIncome)} highlight />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldDisplay label="Has Auto Loan" value={lead.details?.hasAutoLoan === 'yes' ? 'Yes' : 'No'} />
            {lead.details?.hasAutoLoan === 'yes' && (
              <FieldDisplay label="Loan Balance" value={formatCurrency(lead.details?.loanBalance)} />
            )}
            <FieldDisplay label="Credit Score" value={lead.details?.creditScore} highlight />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldDisplay label="Collections" value={lead.details?.hasCollections === 'yes' ? 'Yes' : 'No'} />
              {lead.details?.hasCollections === 'yes' && lead.details?.collectionsDetails && (
                <p className="text-white/60 text-sm mt-1 ml-1">{lead.details.collectionsDetails}</p>
              )}
            </div>
            <div>
              <FieldDisplay label="Open Bankruptcies" value={lead.details?.hasOpenBankruptcies === 'yes' ? 'Yes' : 'No'} />
              {lead.details?.hasOpenBankruptcies === 'yes' && lead.details?.bankruptciesDetails && (
                <p className="text-white/60 text-sm mt-1 ml-1">{lead.details.bankruptciesDetails}</p>
              )}
            </div>
          </div>
          <FieldDisplay
            label="Repossession History"
            value={`${lead.details?.hasRepossession === 'yes' ? 'Yes' : 'No'}${lead.details?.hasRepossession === 'yes' && lead.details?.repossessionTime ? ` - ${lead.details.repossessionTime}` : ''}`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-white/10">
            <FieldDisplay label="Vehicle Interest" value={lead.details?.interestedVehicle} highlight />
            <FieldDisplay label="Budget" value={formatCurrency(lead.details?.budget)} />
            <FieldDisplay label="Down Payment" value={formatCurrency(lead.details?.downPayment)} />
            <FieldDisplay label="Pay Taxes Upfront" value={lead.details?.payTaxesUpfront} />
            <FieldDisplay label="SMS / call consent" value={lead.details?.consent ? 'Yes' : 'No'} />
          </div>
        </div>
      );

    default:
      return null;
  }
}

export default function LeadDetailsContent({
  lead,
  showContactHeader = true,
  showVehicleInterest = true,
  showTradeInVehicle = true,
  hidePersonalInfo = false,
  showDescription = true,
  mergePersonalFrom = null,
}) {
  if (!lead) return null;

  return (
    <div className="space-y-6">
      {showContactHeader && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 border border-blue-500/30">
                <Mail className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <label className="text-xs font-['Manrope'] text-white/60 block">
                  Email
                </label>
                <a
                  href={`mailto:${lead.email}`}
                  className="text-sm font-['Manrope'] text-blue-400 hover:text-blue-300"
                >
                  {lead.email}
                </a>
              </div>
            </div>

            {lead.phone && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 border border-green-500/30">
                  <Phone className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <label className="text-xs font-['Manrope'] text-white/60 block">
                    Phone
                  </label>
                  <a
                    href={`tel:${lead.phone}`}
                    className="text-sm font-['Manrope'] text-green-400 hover:text-green-300"
                  >
                    {lead.phone}
                  </a>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 border border-purple-500/30">
                <Calendar className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <label className="text-xs font-['Manrope'] text-white/60 block">
                  Submitted
                </label>
                <p className="text-sm font-['Manrope'] text-white">
                  {formatLeadDate(lead.created_at)}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10" />
          {lead.contact_quality && (
            <div className={`text-xs font-['Manrope'] px-3 py-2 border ${
              lead.contact_quality.verdict === 'flag'
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                : 'border-white/10 bg-white/5 text-white/70'
            }`}>
              {lead.contact_quality.verdict === 'flag' ? 'Flagged: ' : 'Verified: '}
              Email {lead.contact_quality.email_status || 'unknown'}
              {lead.contact_quality.email_catchall ? ' · catch-all' : ''}
              {lead.contact_quality.email_free ? ' · free webmail' : ''}
              {lead.contact_quality.phone_line_type ? ` · ${lead.contact_quality.phone_line_type}` : ''}
              {lead.contact_quality.phone_line_status ? ` / ${lead.contact_quality.phone_line_status}` : ''}
            </div>
          )}
        </>
      )}

      {renderTypeSpecificDetails(lead, {
        showVehicleInterest,
        showTradeInVehicle,
        hidePersonalInfo,
        showDescription,
        mergePersonalFrom,
      })}
    </div>
  );
}